import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { BadgeCheck, CircleAlert, FileCheck2, ShieldCheck } from "lucide-react";
import logoEmpresa from "@/assets/logo-empresa.svg";
import { formatearMontoOrden } from "@/lib/crm/orden-proveedor-documento";
import { SITE } from "@/lib/site";
import { crmVerificarOrdenPorToken } from "@/server/crm/ordenes-verificacion-publica.fns";

export const Route = createFileRoute("/validar-orden")({
  validateSearch: (search) => ({ token: typeof search.token === "string" ? search.token : "" }),
  component: ValidarOrdenPage,
});

function fecha(value: string | null): string {
  if (!value) return "No registrada";
  const parsed = new Date(value.includes("T") ? value : `${value}T12:00:00`);
  return Number.isNaN(parsed.getTime())
    ? value
    : parsed.toLocaleDateString("es-PE", { dateStyle: "long" });
}

function nombreEstado(estado: string): string {
  return (
    {
      aprobada: "Aprobada",
      recibida: "Recibida",
      entrega_parcial: "Entrega parcial",
      entregada: "Entregada",
      anulada: "Anulada",
    }[estado] ?? estado
  );
}

function ValidarOrdenPage() {
  const { token } = Route.useSearch();
  const consulta = useQuery({
    queryKey: ["verificar-orden", token],
    queryFn: () => crmVerificarOrdenPorToken({ data: { token } }),
    enabled: Boolean(token),
    retry: false,
  });
  const documento = consulta.data;
  const esValido = documento?.encontrada && documento.vigente;

  return (
    <main className="min-h-screen bg-slate-950 px-4 py-10 text-slate-100 sm:px-6">
      <section className="mx-auto w-full max-w-xl overflow-hidden rounded-2xl border border-slate-700 bg-white shadow-2xl">
        <header className="border-b border-slate-700 bg-slate-900 px-6 py-5 text-white sm:px-8">
          <div className="flex items-center gap-3">
            <img
              src={logoEmpresa}
              alt={`Logo de ${SITE.legalName}`}
              className="h-11 w-10 object-contain"
            />
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
                {SITE.legalName}
              </p>
              <h1 className="text-lg font-bold">Verificación de OC / OS</h1>
            </div>
          </div>
        </header>

        <div className="p-6 sm:p-8">
          {!token ? (
            <EstadoInvalido texto="El enlace no contiene un código de verificación válido." />
          ) : consulta.isPending ? (
            <div className="flex items-center gap-3 py-8 text-sm text-slate-600">
              <FileCheck2 className="h-5 w-5 animate-pulse text-primary" /> Verificando documento…
            </div>
          ) : consulta.isError || !documento?.encontrada ? (
            <EstadoInvalido texto="No encontramos una orden válida para este código. Verifique que el QR esté completo." />
          ) : (
            <div className="space-y-6">
              <div
                className={`rounded-xl border p-4 ${esValido ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"}`}
              >
                <div className="flex gap-3">
                  {esValido ? (
                    <ShieldCheck className="mt-0.5 h-6 w-6 shrink-0 text-emerald-600" />
                  ) : (
                    <CircleAlert className="mt-0.5 h-6 w-6 shrink-0 text-red-600" />
                  )}
                  <div>
                    <h2 className={`font-bold ${esValido ? "text-emerald-900" : "text-red-900"}`}>
                      {esValido
                        ? "Documento auténtico y vigente"
                        : "Documento emitido, pero no vigente"}
                    </h2>
                    <p
                      className={`mt-1 text-sm leading-relaxed ${esValido ? "text-emerald-800" : "text-red-800"}`}
                    >
                      {esValido
                        ? "Los datos siguientes coinciden con la versión sellada al aprobar la orden."
                        : `La versión fue emitida, pero la orden hoy figura como ${nombreEstado(documento.estado_actual).toLowerCase()}. No la use como autorización vigente.`}
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid gap-4 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm sm:grid-cols-2">
                <Dato
                  etiqueta="Documento"
                  valor={`${documento.orden.tipo === "servicio" ? "OS" : "OC"} ${documento.orden.numero}`}
                />
                <Dato etiqueta="Estado actual" valor={nombreEstado(documento.estado_actual)} />
                <Dato
                  etiqueta="Proveedor"
                  valor={documento.proveedor.razon_social || "No registrado"}
                />
                <Dato etiqueta="RUC proveedor" valor={documento.proveedor.ruc ?? "No registrado"} />
                <Dato etiqueta="Fecha de emisión" valor={fecha(documento.orden.fecha_emision)} />
                <Dato
                  etiqueta="Total autorizado"
                  valor={formatearMontoOrden(documento.orden.monto, documento.orden.moneda)}
                  destacado
                />
              </div>

              <div className="border-t border-slate-200 pt-4 text-xs text-slate-500">
                <p className="flex items-center gap-1.5 font-semibold text-slate-700">
                  <BadgeCheck className="h-4 w-4 text-emerald-600" /> Versión emitida el{" "}
                  {fecha(documento.emitido_en)}
                </p>
                <p className="mt-2 break-all font-mono text-[10px] leading-relaxed">
                  SHA-256: {documento.hash_sha256}
                </p>
              </div>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}

function Dato({
  etiqueta,
  valor,
  destacado = false,
}: {
  etiqueta: string;
  valor: string;
  destacado?: boolean;
}) {
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{etiqueta}</p>
      <p
        className={`mt-1 ${destacado ? "font-mono text-base font-bold text-slate-950" : "font-medium text-slate-800"}`}
      >
        {valor}
      </p>
    </div>
  );
}

function EstadoInvalido({ texto }: { texto: string }) {
  return (
    <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
      <div className="flex gap-3">
        <CircleAlert className="h-5 w-5 shrink-0" />
        <p>{texto}</p>
      </div>
    </div>
  );
}
